import { Stack, Tab, Tabs } from '@mui/material';
import { RiArrowRightLine } from '@remixicon/react';
import { Button } from '@upheal/design-system/components/Button/Button';
import { RemixIcon } from '@upheal/design-system/components/Icon/RemixIcon';
import { Typography } from '@upheal/design-system/components/Typography/Typography';
import { tabsSx } from '@upheal/design-system/legacy-styles/tabsStyles';
import {
    trackUiEvent,
    uiEventDataAttrs,
} from '@upheal/shared/utils/analytics/ui-tracking';
import { useState } from 'react';
import { formatPriceWithCurrency } from '../../utils/payments';
import { ActivatePlanButton } from './ActivatePlanButton';
import { EnterpriseFeatures } from './EnterpriseFeatures';
import { MeteredFeatures } from './MeteredFeatures';
import { MeteredPriceHeader } from './MeteredPriceHeader';
import { PromoBannerCard } from './PromoBannerCard';

type PlanCategory = 'individual' | 'group' | 'enterprise';

type MeteredPromoBannerProps = {
    pricePerSession: number;
    providerCapPrice: number;
    currency: string;
    uiLocation?: string;
    onContactSales: () => void;
};

export const MeteredPromoBanner = ({
    pricePerSession,
    providerCapPrice,
    currency,
    uiLocation = 'Billing',
    onContactSales,
}: MeteredPromoBannerProps) => {
    const [planCategory, setPlanCategory] =
        useState<PlanCategory>('individual');

    const formattedPrice = formatPriceWithCurrency(pricePerSession, currency);
    const formattedCap = formatPriceWithCurrency(providerCapPrice, currency);

    return (
        <Stack alignItems="center" gap={3} width="100%">
            <Tabs
                value={planCategory}
                centered
                onChange={(_, newValue: PlanCategory) => {
                    trackUiEvent(
                        'Click',
                        newValue === 'individual'
                            ? 'Show Solo Pricing'
                            : 'Show Custom Pricing',
                        uiLocation,
                    );
                    setPlanCategory(newValue);
                }}
                sx={{ ...tabsSx, mb: 0 }}
            >
                <Tab label="Individual" value="individual" />
                <Tab label="Group" value="group" />
                <Tab label="Enterprise" value="enterprise" />
            </Tabs>

            {planCategory === 'individual' && (
                <PromoBannerCard
                    header={
                        <MeteredPriceHeader
                            formattedPrice={formattedPrice}
                            capLabel={`Capped at ${formattedCap}/month`}
                        />
                    }
                    cta={
                        <ActivatePlanButton
                            uiLocation={uiLocation}
                            uiProperty="individual"
                        />
                    }
                    features={<MeteredFeatures uiLocation={uiLocation} />}
                />
            )}

            {planCategory === 'group' && (
                <PromoBannerCard
                    header={
                        <MeteredPriceHeader
                            formattedPrice={formattedPrice}
                            capLabel={`Capped at ${formattedCap}/mo per provider`}
                        />
                    }
                    cta={
                        <ActivatePlanButton
                            uiLocation={uiLocation}
                            uiProperty="group"
                        />
                    }
                    features={<MeteredFeatures uiLocation={uiLocation} />}
                />
            )}

            {planCategory === 'enterprise' && (
                <PromoBannerCard
                    header={
                        <Typography variant="h4" fontWeight="semibold">
                            Enterprise plan
                        </Typography>
                    }
                    cta={
                        <Button
                            variant="solid"
                            colorScheme="accent2"
                            size="md"
                            onClick={onContactSales}
                            {...uiEventDataAttrs(
                                'Contact Sales',
                                uiLocation,
                                'Enterprise Plan',
                            )}
                        >
                            <Typography slot="label">Contact us</Typography>
                            <RemixIcon
                                icon={RiArrowRightLine}
                                slot="trailingItem"
                            />
                        </Button>
                    }
                    features={<EnterpriseFeatures />}
                />
            )}
        </Stack>
    );
};
